import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  IoArchiveOutline,
  IoBulbOutline,
  IoChevronDownOutline,
  IoCloseOutline,
  IoCreateOutline,
  IoIdCardOutline,
  IoListOutline,
  IoPeopleOutline,
  IoPersonOutline,
  IoSearchOutline,
  IoSparklesOutline,
  IoTimeOutline,
  IoTrashOutline,
} from 'react-icons/io5';

// Memory types the backend tags each memory with, grouped the way Atlas reads them.
const WORK_TYPES = ['preference', 'procedure'];
const OTHER_TYPES = ['project', 'episode', 'artifact'];

export const TYPE_LABELS = {
  preference: 'Preference',
  procedure: 'Procedure',
  relationship: 'Person',
  identity: 'About you',
  project: 'Project',
  episode: 'History',
  artifact: 'Document',
};

export const WORK_TYPE_OPTIONS = [
  { value: 'preference', label: 'Preference', placeholder: 'e.g., Keep summaries to one page with the numbers up top' },
  { value: 'procedure', label: 'Procedure', placeholder: 'e.g., Pull comps within a mile before looking wider' },
];

const COPY = {
  personal: {
    descriptionTitle: 'About your business',
    summaryTitle: 'What Atlas knows about your work',
    summaryHint: 'Atlas writes this from your past conversations and reads it at the start of each new one.',
    summaryEmpty: 'Atlas builds this summary from your conversations overnight.',
    workTitle: 'How you want work done',
    workHint: 'Standing instructions Atlas follows in every conversation.',
    workEmpty: 'Nothing here yet. Add how you like reports, numbers or reviews handled.',
    peopleTitle: 'People',
    peopleHint: 'Colleagues, partners and clients Atlas should recognize.',
    peopleEmpty: 'No people saved yet. Add someone Atlas should know when their name comes up.',
    peoplePlaceholder: 'e.g., Dana Ortiz runs land acquisition for our Raleigh division',
    identityTitle: 'About you',
    identityHint: 'Your role, market and background.',
    identityEmpty: 'Nothing about you yet. Add your role or anything Atlas should keep in mind.',
    identityPlaceholder: 'e.g., I lead land acquisition for a builder in the Triangle',
    unsortedTitle: 'Other notes',
    unsortedHint: 'Saved without a category.',
    otherTitle: 'Other things Atlas remembers',
    otherHint: 'Notes from past work: projects, earlier conversations and documents.',
    otherEmpty: 'Nothing else saved yet. Atlas keeps notes here on projects and past work as you chat.',
    savedByUser: 'Saved by you',
  },
  company: {
    descriptionTitle: 'About the company',
    summaryTitle: 'What Atlas knows about the company',
    summaryHint: "Atlas writes this from the company's conversations and reads it at the start of each company conversation.",
    summaryEmpty: "Atlas builds this summary from the company's conversations overnight.",
    workTitle: 'How the team wants work done',
    workHint: 'Standing instructions Atlas follows in company mode.',
    workEmpty: 'Nothing here yet.',
    peopleTitle: 'People',
    peopleHint: 'Colleagues, partners and clients Atlas should recognize.',
    peopleEmpty: 'No people saved yet.',
    identityTitle: 'Company facts',
    identityHint: 'Who the company is and where it works.',
    identityEmpty: 'No company facts saved yet.',
    unsortedTitle: 'Other notes',
    unsortedHint: 'Add anything else Atlas should know in company mode.',
    otherTitle: 'Other things Atlas remembers',
    otherHint: 'Notes from past company work: projects, earlier conversations and documents.',
    otherEmpty: 'Nothing else saved yet. Atlas keeps notes here on projects and past work as the team chats.',
    savedByUser: 'Saved by your team',
  },
};

export function getProfileCopy(isCompanyScope) {
  return isCompanyScope ? COPY.company : COPY.personal;
}

export function normalizeMemory(memory) {
  if (!memory || typeof memory !== 'object') return memory;
  return { ...memory, id: memory.memory_id ?? memory.id };
}

function memoryType(memory) {
  return String(memory?.type || '').trim().toLowerCase();
}

/** New backends tag memories with a type (or say so); old ones return a flat, untyped list. */
export function isStructuredProfile(data) {
  if (!data || typeof data !== 'object') return false;
  if (data.structured === true || (data.profile && typeof data.profile === 'object')) return true;
  return Array.isArray(data.memories) && data.memories.some((memory) => memoryType(memory));
}

export function groupMemories(memories = []) {
  const groups = { work: [], people: [], identity: [], unsorted: [], other: [] };
  memories.forEach((memory) => {
    const type = memoryType(memory);
    if (WORK_TYPES.includes(type)) groups.work.push(memory);
    else if (type === 'relationship') groups.people.push(memory);
    else if (type === 'identity') groups.identity.push(memory);
    else if (OTHER_TYPES.includes(type)) groups.other.push(memory);
    else groups.unsorted.push(memory);
  });
  return groups;
}

/** Apply an edit response. The backend may return the memory under a new id; replace the item. */
export function mergeUpdatedMemory(previous, updated, text) {
  if (!updated || typeof updated !== 'object') return { ...previous, memory: text };
  const id = updated.memory_id ?? updated.id ?? previous.id;
  const next = { ...previous, ...updated, id };
  if ('memory_id' in next) next.memory_id = id;
  return next;
}

function entityText(entity) {
  if (entity == null) return '';
  if (typeof entity === 'string' || typeof entity === 'number') return String(entity);
  if (typeof entity === 'object') {
    return [entity.name, entity.value, entity.label, entity.type].filter(Boolean).join(' ');
  }
  return '';
}

export function filterMemories(memories, query) {
  const terms = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return memories;
  return memories.filter((memory) => {
    const haystack = [
      memory.memory,
      memory.detail,
      TYPE_LABELS[memoryType(memory)],
      ...(Array.isArray(memory.entities) ? memory.entities.map(entityText) : []),
    ].filter(Boolean).join(' ').toLowerCase();
    return terms.every((term) => haystack.includes(term));
  });
}

function formatShortDate(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function SectionHeader({ icon: Icon, title, count, showCount = count > 0 }) {
  return (
    <div className="builder-profile-section-header">
      <h3 className="builder-profile-section-label">
        <Icon size={16} aria-hidden="true" />
        <span>{title}</span>
      </h3>
      {showCount && <span className="builder-profile-memory-count">{count}</span>}
    </div>
  );
}

function EmptyLine({ children }) {
  return (
    <div className="builder-profile-empty builder-profile-empty-compact">
      <span>{children}</span>
    </div>
  );
}

const markdownComponents = {
  a: ({ node, children, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer">{children}</a>
  ),
};

export function ProfileSummary({ profile, copy }) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const bodyRef = useRef(null);
  const bodyId = useId();
  const text = typeof profile?.text === 'string' ? profile.text.trim() : '';

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || expanded) return;
    setOverflowing(el.scrollHeight > el.clientHeight + 4);
  }, [text, expanded]);

  const compiled = formatShortDate(profile?.compiled_at);
  const conversations = Number(profile?.conversations) || 0;
  const conversationText = conversations
    ? `${conversations.toLocaleString('en-US')} conversation${conversations === 1 ? '' : 's'}`
    : '';
  let meta = '';
  if (compiled && conversationText) meta = `Compiled ${compiled} from ${conversationText}`;
  else if (compiled) meta = `Compiled ${compiled}`;
  else if (conversationText) meta = `From ${conversationText}`;

  return (
    <section className="builder-profile-section" aria-label={copy.summaryTitle}>
      <SectionHeader icon={IoSparklesOutline} title={copy.summaryTitle} />
      <p className="builder-profile-section-hint">{copy.summaryHint}</p>
      {text ? (
        <div className="builder-profile-summary">
          <div
            id={bodyId}
            ref={bodyRef}
            className={`builder-profile-summary-body${expanded ? '' : ' collapsed'}${!expanded && overflowing ? ' faded' : ''}`}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{text}</ReactMarkdown>
          </div>
          {(meta || overflowing || expanded) && (
            <div className="builder-profile-summary-footer">
              <span className="builder-profile-summary-meta">{meta}</span>
              {(overflowing || expanded) && (
                <button
                  type="button"
                  className="builder-profile-edit-btn"
                  aria-expanded={expanded}
                  aria-controls={bodyId}
                  onClick={() => setExpanded((value) => !value)}
                >
                  {expanded ? 'Show less' : 'Show all'}
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <EmptyLine>{copy.summaryEmpty}</EmptyLine>
      )}
    </section>
  );
}

function SourceMarker({ createdBy, copy }) {
  if (createdBy == null || createdBy === '') return null;
  const byUser = String(createdBy).toLowerCase() === 'user';
  return (
    <span className={`builder-profile-memory-source ${byUser ? 'by-user' : 'by-atlas'}`}>
      {byUser ? <IoPersonOutline size={11} aria-hidden="true" /> : <IoSparklesOutline size={11} aria-hidden="true" />}
      {byUser ? copy.savedByUser : 'Saved by Atlas'}
    </span>
  );
}

export function MemoryItem({
  memory,
  copy,
  showTypeLabel = false,
  canEdit = false,
  allowDetail = false,
  editing,
  saving,
  deletingId,
  onStartEdit,
  onChangeEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
}) {
  const isEditing = canEdit && editing && String(editing.id) === String(memory.id);
  const editRef = useRef(null);
  const type = memoryType(memory);

  // Focus the editor with the caret at the end of the text.
  useEffect(() => {
    const el = editRef.current;
    if (!isEditing || !el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [isEditing]);
  const date = formatShortDate(memory.created_at);
  const isDeleting = deletingId != null && String(deletingId) === String(memory.id);
  const preview = String(memory.memory || '').slice(0, 60);

  const handleEditKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onCancelEdit();
    } else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      onSaveEdit();
    }
  };

  return (
    <li className={`builder-profile-memory-item${isEditing ? ' editing' : ''}`}>
      <div className="builder-profile-memory-content">
        {isEditing ? (
          <div className="builder-profile-memory-editor">
            <textarea
              className="builder-profile-textarea builder-profile-memory-edit"
              value={editing.memory}
              onChange={(event) => onChangeEdit({ memory: event.target.value })}
              onKeyDown={handleEditKeyDown}
              maxLength={500}
              aria-label="Edit memory"
              ref={editRef}
            />
            {allowDetail && (
              <textarea
                className="builder-profile-textarea builder-profile-memory-edit builder-profile-memory-edit-detail"
                value={editing.detail}
                onChange={(event) => onChangeEdit({ detail: event.target.value })}
                onKeyDown={handleEditKeyDown}
                maxLength={2000}
                placeholder="More detail (optional)"
                aria-label="Edit memory detail"
              />
            )}
            <div className="builder-profile-memory-edit-actions">
              <button type="button" className="builder-profile-btn builder-profile-btn-ghost" onClick={onCancelEdit}>
                Cancel
              </button>
              <button
                type="button"
                className="builder-profile-btn builder-profile-btn-primary"
                onClick={onSaveEdit}
                disabled={saving || !String(editing.memory || '').trim()}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="builder-profile-memory-text">{memory.memory}</p>
            {memory.detail ? <p className="builder-profile-memory-detail">{memory.detail}</p> : null}
          </>
        )}
        <div className="builder-profile-memory-meta">
          {showTypeLabel && TYPE_LABELS[type] && (
            <span className={`builder-profile-type-label type-${type}`}>{TYPE_LABELS[type]}</span>
          )}
          {date && (
            <span className="builder-profile-memory-date">
              <IoTimeOutline size={12} aria-hidden="true" />
              {date}
            </span>
          )}
          <SourceMarker createdBy={memory.created_by} copy={copy} />
        </div>
      </div>
      {!isEditing && (
        <div className="builder-profile-memory-actions">
          {canEdit && (
            <button
              type="button"
              className="builder-profile-memory-icon-btn"
              onClick={() => onStartEdit(memory)}
              title="Edit"
              aria-label={`Edit: ${preview}`}
            >
              <IoCreateOutline size={14} />
            </button>
          )}
          <button
            type="button"
            className="builder-profile-memory-delete"
            onClick={() => onDelete(memory.id)}
            disabled={isDeleting}
            title="Remove"
            aria-label={`Remove: ${preview}`}
          >
            {isDeleting ? <span className="builder-profile-spinner-sm" /> : <IoTrashOutline size={14} />}
          </button>
        </div>
      )}
    </li>
  );
}

export function MemoryComposer({ value, onChange, onSubmit, placeholder, label, saving, typeOptions, selectedType, onSelectType }) {
  const activeOption = typeOptions?.find((option) => option.value === selectedType);
  return (
    <div className="builder-profile-composer">
      {typeOptions && (
        <div className="builder-profile-type-chips" role="group" aria-label="Kind of instruction">
          {typeOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`builder-profile-type-chip${option.value === selectedType ? ' active' : ''}`}
              aria-pressed={option.value === selectedType}
              onClick={() => onSelectType(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
      <div className="builder-profile-memory-compose">
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
              event.preventDefault();
              onSubmit();
            }
          }}
          placeholder={activeOption?.placeholder || placeholder}
          aria-label={label}
          maxLength={500}
        />
        <button
          type="button"
          className="builder-profile-btn builder-profile-btn-primary"
          onClick={onSubmit}
          disabled={saving || !value.trim()}
        >
          Add
        </button>
      </div>
    </div>
  );
}

function MemoryList({ items, flow = false, ...itemProps }) {
  return (
    <ul className={`builder-profile-memories-list${flow ? ' builder-profile-memories-list-flow' : ''}`}>
      {items.map((memory) => (
        <MemoryItem key={memory.id} memory={memory} {...itemProps} />
      ))}
    </ul>
  );
}

function OtherMemoriesSection({ items, copy, itemProps }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const panelId = useId();
  const filtered = useMemo(() => filterMemories(items, query), [items, query]);

  return (
    <section className="builder-profile-section" aria-label={copy.otherTitle}>
      <div className="builder-profile-section-header">
        <h3 className="builder-profile-section-label builder-profile-section-label-toggle">
          <button
            type="button"
            className="builder-profile-section-toggle"
            aria-expanded={open}
            aria-controls={panelId}
            onClick={() => setOpen((value) => !value)}
          >
            <IoArchiveOutline size={16} aria-hidden="true" />
            <span>{copy.otherTitle}</span>
            <IoChevronDownOutline size={14} aria-hidden="true" className={`builder-profile-chevron${open ? ' open' : ''}`} />
          </button>
        </h3>
        <span className="builder-profile-memory-count">{items.length}</span>
      </div>
      <p className="builder-profile-section-hint">{copy.otherHint}</p>
      {open && (
        <div id={panelId} className="builder-profile-other-panel">
          {items.length === 0 ? (
            <EmptyLine>{copy.otherEmpty}</EmptyLine>
          ) : (
            <>
              <div className="builder-profile-search">
                <IoSearchOutline size={14} aria-hidden="true" />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search these notes"
                  aria-label={`Search ${copy.otherTitle.toLowerCase()}`}
                />
                {query && (
                  <button type="button" className="builder-profile-search-clear" onClick={() => setQuery('')} aria-label="Clear search">
                    <IoCloseOutline size={14} />
                  </button>
                )}
              </div>
              {filtered.length === 0 ? (
                <EmptyLine>Nothing matches “{query.trim()}”.</EmptyLine>
              ) : (
                <MemoryList items={filtered} showTypeLabel {...itemProps} canEdit={false} />
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Sections 2-6 of the profile panel, in the order Atlas reads them at the start of a conversation.
 * Personal scope: typed add/edit/delete. Company scope: one untyped composer (the company API takes no type),
 * edit/delete through the company calls.
 */
export function StructuredMemorySections({
  isCompanyScope,
  profile,
  memories,
  drafts,
  onChangeDraft,
  onAddTyped,
  companyDraft,
  onChangeCompanyDraft,
  onAddCompany,
  saving,
  editing,
  deletingId,
  onStartEdit,
  onChangeEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
}) {
  const copy = getProfileCopy(isCompanyScope);
  const groups = useMemo(() => groupMemories(memories), [memories]);
  const itemProps = {
    copy,
    editing,
    saving,
    deletingId,
    onStartEdit,
    onChangeEdit,
    onSaveEdit,
    onCancelEdit,
    onDelete,
    canEdit: true,
    allowDetail: !isCompanyScope,
  };
  const canAddTyped = !isCompanyScope;

  const typedSection = ({ key, icon, title, hint, empty, items, placeholder, typeOptions, showTypeLabel = false }) => (
    <section className="builder-profile-section" aria-label={title}>
      <SectionHeader icon={icon} title={title} count={items.length} />
      <p className="builder-profile-section-hint">{hint}</p>
      {canAddTyped && (
        <MemoryComposer
          value={drafts[key].text}
          onChange={(text) => onChangeDraft(key, { text })}
          onSubmit={() => onAddTyped(key)}
          placeholder={placeholder}
          label={`Add to ${title.toLowerCase()}`}
          saving={saving}
          typeOptions={typeOptions}
          selectedType={drafts[key].type}
          onSelectType={(type) => onChangeDraft(key, { type })}
        />
      )}
      {items.length === 0 ? (
        <EmptyLine>{empty}</EmptyLine>
      ) : (
        <MemoryList items={items} flow showTypeLabel={showTypeLabel} {...itemProps} />
      )}
    </section>
  );

  return (
    <>
      <ProfileSummary profile={profile} copy={copy} />
      {typedSection({
        key: 'work',
        icon: IoListOutline,
        title: copy.workTitle,
        hint: copy.workHint,
        empty: copy.workEmpty,
        items: groups.work,
        typeOptions: WORK_TYPE_OPTIONS,
        showTypeLabel: true,
      })}
      {typedSection({
        key: 'people',
        icon: IoPeopleOutline,
        title: copy.peopleTitle,
        hint: copy.peopleHint,
        empty: copy.peopleEmpty,
        items: groups.people,
        placeholder: copy.peoplePlaceholder,
      })}
      {typedSection({
        key: 'identity',
        icon: IoIdCardOutline,
        title: copy.identityTitle,
        hint: copy.identityHint,
        empty: copy.identityEmpty,
        items: groups.identity,
        placeholder: copy.identityPlaceholder,
      })}
      {(isCompanyScope || groups.unsorted.length > 0) && (
        <section className="builder-profile-section" aria-label={copy.unsortedTitle}>
          <SectionHeader icon={IoBulbOutline} title={copy.unsortedTitle} count={groups.unsorted.length} />
          <p className="builder-profile-section-hint">{copy.unsortedHint}</p>
          {isCompanyScope && (
            <MemoryComposer
              value={companyDraft}
              onChange={onChangeCompanyDraft}
              onSubmit={onAddCompany}
              placeholder="Add a company memory..."
              label="Add a company memory"
              saving={saving}
            />
          )}
          {groups.unsorted.length > 0 && <MemoryList items={groups.unsorted} flow {...itemProps} />}
        </section>
      )}
      <OtherMemoriesSection items={groups.other} copy={copy} itemProps={itemProps} />
    </>
  );
}
